import { Action, Dispatch } from 'redux';

import { TICKETS_URL } from 'appConstants';
import { ApiRequest, ImmutableQuery } from 'services';
import { TicketExport } from 'types';

const defaultTicketCount = -1;
/* Types */

export interface ExportTicketsAction extends Action {
  readonly open?: number;
  readonly closed?: number;
  readonly pendingcustomer?: number;
  readonly status?: string;
  readonly count?: number;
  readonly isExportModalVisible?: boolean;
  readonly isExportToastVisible?: boolean;
  readonly isExportFailed?: boolean;
}

export interface ExportTickets {
  open: number;
  closed: number;
  pendingcustomer: number;
  isExportModalVisible: boolean;
  isExportToastVisible: boolean;
  isExportFailed: boolean;
}

const initialState = {
  open: defaultTicketCount,
  closed: defaultTicketCount,
  pendingcustomer: defaultTicketCount,
  isExportModalVisible: false,
  isExportToastVisible: false,
  isExportFailed: false,
} as ExportTickets;

/* Actions */

enum Actions {
  SET_EXPORT_MODAL_VISIBILITY = 'SET_EXPORT_MODAL_VISIBILITY',
  SET_EXPORT_TOAST_VISIBILITY = 'SET_EXPORT_TOAST_VISIBILITY',
  SET_TICKET_COUNT = 'SET_TICKET_COUNT',
  SET_RATE_LIMIT_FLAG = 'SET_RATE_LIMIT_FLAG',
}

const setExportModalVisibility = (
  isExportModalVisible: boolean,
): ExportTicketsAction => ({
  type: Actions.SET_EXPORT_MODAL_VISIBILITY,
  isExportModalVisible,
});

const setExportToastVisibility = (
  isExportToastVisible: boolean,
): ExportTicketsAction => ({
  type: Actions.SET_EXPORT_TOAST_VISIBILITY,
  isExportToastVisible,
});

const setTicketCount = (
  count: number,
  status: string,
): ExportTicketsAction => ({
  type: Actions.SET_TICKET_COUNT,
  count,
  status,
});

const setExportFailed = (isExportFailed: boolean): ExportTicketsAction => ({
  type: Actions.SET_RATE_LIMIT_FLAG,
  isExportFailed,
});

const exportTicketsReducer = (
  state = initialState,
  action: ExportTicketsAction,
) => {
  switch (action.type) {
    case Actions.SET_EXPORT_MODAL_VISIBILITY:
      return {
        ...state,
        isExportModalVisible: action.isExportModalVisible,
      };
    case Actions.SET_EXPORT_TOAST_VISIBILITY:
      return {
        ...state,
        isExportToastVisible: action.isExportToastVisible,
        isExportFailed: false,
      };
    case Actions.SET_RATE_LIMIT_FLAG:
      return {
        ...state,
        isExportFailed: action.isExportFailed,
      };
    case Actions.SET_TICKET_COUNT:
      return {
        ...state,
        ...(action.status && { [action.status]: action.count }),
      };
    default:
      return state;
  }
};

/* Thunks */
const exportTickets = (ticketStatus: string) => async (
  dispatch: Dispatch<ExportTicketsAction>,
) => {
  const onSuccess = async (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = ticketStatus + '_Tickets.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    dispatch(setExportToastVisibility(false));
    dispatch(setExportFailed(false));
  };

  const onError = () => {
    dispatch(setExportToastVisibility(true));
    dispatch(setExportFailed(true));
  };

  ApiRequest.get(
    `${TICKETS_URL}/export${ImmutableQuery.fromObject({
      status: ticketStatus,
    }).toQueryString()}`,
    {
      onSuccess,
      onError,
    },
  );

  dispatch(setExportToastVisibility(true));
};

const getTicketCount = (ticketStatus: string) => async (
  dispatch: Dispatch<ExportTicketsAction>,
) => {
  const onSuccess = (exportTicket: TicketExport) => {
    dispatch(
      setTicketCount(
        exportTicket.total,
        ticketStatus
          .split(' ')
          .join('')
          .toLowerCase(),
      ),
    );
  };

  const onError = () => {
    dispatch(
      setTicketCount(
        defaultTicketCount,
        ticketStatus
          .split(' ')
          .join('')
          .toLowerCase(),
      ),
    );
  };

  await ApiRequest.get(
    TICKETS_URL +
      ImmutableQuery.fromObject({
        status: ticketStatus,
        limit: 1,
      }).toQueryString(),
    {
      onError,
      onSuccess,
    },
  );
};

export {
  getTicketCount,
  exportTickets,
  setExportModalVisibility,
  setExportToastVisibility,
  exportTicketsReducer,
};
